import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useTranslation, Trans } from 'react-i18next';
import { setResultPriceToShow } from '@/actions/search';
import { ssoError } from '@/actions/sso';
import { triggerSignInWidget, triggerSignOutWidget } from 'utils/session';
import usePurchase from 'hooks/store/usePurchase';
import useSSO from 'hooks/store/useSSO';
import useWhitelabelFeatures from 'hooks/whitelabel/useWhitelabelFeatures';
import { DialogMessage, Text, Icon } from '@reservamos/elements';
import useTheme from 'hooks/whitelabel/useWhitelabelTheme';
import { trackEvent } from 'user-analytics';
import transportBusIcon from 'images/icons/transport-bus.svg';
import {
  clearDotersFromState,
  logoutReserhubDueToDotersUnauthorized,
} from '@/actions/reserhubAccount';
import { useReserhubAccount } from '../../context/ReserhubAccount';
import dotersLogo from '../../images/doters/greendoters.svg';
import { DOTERS_LOGIN_ALERT_SHOWN } from '../../constants/TrackEvents';

/** SSO auth modal to notify the user that it needs to log in */
export default function SSOAuthDialog() {
  const [loggingWithDoters, setLoggingWithDoters] = useState(false);
  const { token: purchaseToken } = usePurchase();
  const { logos } = useTheme();
  const features = useWhitelabelFeatures();
  const sso = useSSO();
  const {
    connectedAccounts,
    loginWithDoters,
    enabled: isReserhubAccountEnabled,
  } = useReserhubAccount();
  const verifyClicked = useRef(false);

  const connectedDotersAccount = connectedAccounts?.find(
    (account) => account.provider === 'doters',
  );
  const { t: tLoyalty } = useTranslation('loyalty');
  const dispatch = useDispatch();
  const history = useHistory();
  const loyaltyName = 'siempreplus';
  const eventSent = useRef(false);

  useEffect(() => {
    if (sso.error === 401 && features.USE_SSO && !eventSent.current) {
      eventSent.current = true;
      trackEvent(DOTERS_LOGIN_ALERT_SHOWN, {
        'Is Unified Account': isReserhubAccountEnabled,
      });
    }
  }, [sso.error, features.USE_SSO, isReserhubAccountEnabled]);

  if (!features.USE_SSO || sso.error !== 401) return null;

  /**
   * Handle the accept button click
   */
  const onAccept = async () => {
    if (verifyClicked.current) return;

    // If the user has a connected Doters account, log in with Doters using unified account
    if (connectedDotersAccount) {
      verifyClicked.current = true;
      try {
        setLoggingWithDoters(true);
        const url = await loginWithDoters();
        dispatch(clearDotersFromState());
        window.location.href = url;
      } catch (error) {
        // Do nothing, let the action to handle error
      } finally {
        setLoggingWithDoters(false);
        verifyClicked.current = false;
      }
      return;
    }

    // If the user doesn't have a connected Doters account, use legacy SSO flow
    verifyClicked.current = true;
    setLoggingWithDoters(true);
    triggerSignOutWidget(loyaltyName);
    triggerSignInWidget(loyaltyName);
  };

  /**
   * Handle the cancel button click
   */
  const onCancel = async () => {
    if (verifyClicked.current) return;
    verifyClicked.current = true;

    try {
      // If the user has a connected Doters account, log out of Doters using unified account
      if (connectedDotersAccount) {
        await dispatch(logoutReserhubDueToDotersUnauthorized());
      }

      // If the unified account is disabled, use legacy SSO flow
      if (!isReserhubAccountEnabled) triggerSignOutWidget(loyaltyName);

      // Reset SSO error and results prices
      dispatch(setResultPriceToShow('normal'));
      dispatch(ssoError(false));
      if (purchaseToken) history.replace(`/purchase/${purchaseToken}/passengers`);
    } finally {
      verifyClicked.current = false;
    }
  };

  const hasIsotype = logos?.isotype && logos?.isotype !== null;

  return (
    <DialogMessage
      title={tLoyalty('auth_error')}
      primaryButton={loggingWithDoters ? tLoyalty('verifying_doters') : tLoyalty('verify_now')}
      secondaryButton={tLoyalty('sign_out_doters')}
      onClickPrimary={onAccept}
      onClickSecondary={onCancel}
      dialogType="primary"
      showFade
      iconType="freeCancellation"
    >
      <Text>
        <Trans i18nKey="loyalty:need_sign_in_doters" components={{ strong: <strong /> }} />
      </Text>
      <div className="flex justify-center mb-5 mt-8 gap-2">
        <div className="flex items-center justify-center gap-4">
          <div className="border border-dashed border-2 border-gray-300 rounded-xl w-12 h-12 flex items-center justify-center">
            <img src={dotersLogo} alt="" className="w-4 object-cover" />
          </div>
          <Icon type="ArrowForward" size="S" color="grayMedium" />
          <div className="border border-primary-regular border-2 rounded-xl w-12 h-12 flex items-center justify-center">
            <img
              src={hasIsotype ? logos?.isotype : transportBusIcon}
              alt=""
              className="w-4 object-cover"
            />
          </div>
        </div>
      </div>
    </DialogMessage>
  );
}
